import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  IconBrandLinkedin,
  IconBrandGithub,
  IconBrandTwitter,
  IconBrandInstagram,
  IconBrandFacebook,
  IconBrandDribbble,
  IconLink,
  IconCode,
  IconMapPin,
  IconMail,
  IconPhone,
  IconSchool,
  IconBriefcase,
  IconCertificate,
  IconCalendar,
  IconUser,
  IconDownload,
  IconCircleCheck,
  IconGlobe,
  IconExternalLink,
  IconTrophy,
  IconAward,
  IconHeart,
  IconBook,
  IconTarget,
  IconShield,
  IconPresentation,
  IconUsers,
} from '@tabler/icons-react'
import { supabase } from '@/services/supabase'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ContributionHeatmap } from '@/components/candidate/contribution-heatmap'
import { SkillRadar } from '@/components/candidate/skill-radar'
import { PageLoader } from '@/components/page-loader'

// ═══════════════════════════════════════════
// Route registration
// ═══════════════════════════════════════════
export const Route = createFileRoute('/portfolio/$userId')({
  component: PublicPortfolio,
})

// ═══════════════════════════════════════════
// Platform Mapping for Social Links
// ═══════════════════════════════════════════
const PLATFORM_MAP: Record<string, { label: string; icon: React.ElementType }> =
  {
    linkedin: { label: 'LinkedIn', icon: IconBrandLinkedin },
    github: { label: 'Github', icon: IconBrandGithub },
    twitter: { label: 'Twitter / X', icon: IconBrandTwitter },
    portfolio: { label: 'Portfolio', icon: IconLink },
    leetcode: { label: 'Leetcode', icon: IconCode },
    codechef: { label: 'Codechef', icon: IconCode },
    codeforces: { label: 'Codeforces', icon: IconCode },
    hackerrank: { label: 'Hackerrank', icon: IconCode },
    kaggle: { label: 'Kaggle', icon: IconCode },
    dribbble: { label: 'Dribbble', icon: IconBrandDribbble },
    instagram: { label: 'Instagram', icon: IconBrandInstagram },
    facebook: { label: 'Facebook', icon: IconBrandFacebook },
    googleplus: { label: 'Google Plus', icon: IconGlobe },
  }

function formatDate(dateStr: string) {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return dateStr
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  } catch {
    return dateStr
  }
}

function PublicPortfolio() {
  const { userId } = Route.useParams()

  // Fetch consolidated public portfolio via RPC (Security Definer)
  const {
    data: portfolioData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['public-portfolio', userId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_public_portfolio', {
        p_slug_or_id: userId,
      })
      if (error) throw error
      return data
    },
    enabled: !!userId,
  })

  const onboarding = portfolioData?.onboarding
  const projects = portfolioData?.projects || []
  const skills = portfolioData?.skills || []
  const languages = portfolioData?.languages || []
  const subjects = portfolioData?.subjects || []
  const githubStats = portfolioData?.github_stats
  const profile = portfolioData
    ? { is_email_verified: portfolioData.is_email_verified }
    : null

  const skillData = React.useMemo(() => {
    if (!skills || skills.length === 0) return []
    return skills.slice(0, 6).map((skill: any) => {
      let level = 25
      if (skill.proficiency === 'Intermediate') level = 50
      if (skill.proficiency === 'Advance' || skill.proficiency === 'Advanced')
        level = 75
      if (
        skill.proficiency === 'Professional' ||
        skill.proficiency === 'Expert'
      )
        level = 100
      return { name: skill.name, level }
    })
  }, [skills])

  if (isLoading) {
    return <PageLoader color='hsl(var(--primary))' />
  }

  if (error || !onboarding) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-slate-950'>
        <Card className='w-full max-w-md border border-border bg-white p-8 text-center shadow-xl dark:bg-zinc-900'>
          <h1 className='mb-4 text-2xl font-bold text-slate-900 dark:text-white'>
            Profile Not Found
          </h1>
          <p className='mb-6 text-slate-600 dark:text-slate-400'>
            The requested candidate portfolio could not be found or is private.
          </p>
        </Card>
      </div>
    )
  }

  // Parse Resume URL JSON Array to find primary
  let primaryResumeUrl = ''
  if (onboarding.resume_url) {
    try {
      const parsed = JSON.parse(onboarding.resume_url)
      if (Array.isArray(parsed)) {
        const primary =
          parsed.find((r) => r.isSynced) ||
          parsed.find((r) => r.isPrimary) ||
          parsed[0]
        primaryResumeUrl = primary?.url || ''
      } else {
        primaryResumeUrl = onboarding.resume_url
      }
    } catch {
      primaryResumeUrl = onboarding.resume_url
    }
  }

  // Filter out portfolio_slug and abc_id from socials rendering
  const socialLinks = { ...(onboarding.social_links || {}) }
  delete socialLinks.portfolio_slug
  delete socialLinks.abc_id

  const fullName =
    `${onboarding.first_name || ''} ${onboarding.last_name || ''}`.trim()
  const allExperiences = onboarding.experiences || []

  // Categorized timeline items
  const jobs = allExperiences.filter((e: any) =>
    ['fulltime', 'internship', 'parttime'].includes(e.type)
  )
  const pors = allExperiences.filter((e: any) => e.type === 'por')
  const certifications = allExperiences.filter(
    (e: any) => e.type === 'certification'
  )
  const volunteering = allExperiences.filter(
    (e: any) => e.type === 'volunteering'
  )
  const extracurriculars = allExperiences.filter(
    (e: any) => e.type === 'extracurricular'
  )

  // Academic Accomplishments
  const awards = allExperiences.filter((e: any) => e.type === 'award')
  const competitions = allExperiences.filter(
    (e: any) => e.type === 'competition'
  )
  const scholarships = allExperiences.filter(
    (e: any) => e.type === 'scholarship'
  )
  const testScores = allExperiences.filter((e: any) => e.type === 'test_score')

  // Research & IP
  const patents = allExperiences.filter((e: any) => e.type === 'patent')
  const publications = allExperiences.filter(
    (e: any) => e.type === 'publication'
  )
  const conferences = allExperiences.filter((e: any) => e.type === 'conference')

  const currentEdu = onboarding.current_education || {}
  const previousEdu = onboarding.previous_education || []

  return (
    <div className='min-h-screen bg-slate-50 px-4 py-10 text-foreground transition-colors duration-200 dark:bg-zinc-950 sm:px-6 md:px-8'>
      <div className='mx-auto max-w-6xl space-y-8'>
        {/* ── Header Card ── */}
        <Card className='relative overflow-hidden rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
          {/* Subtle gradient accent bar */}
          <div className='h-2.5 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500' />

          <div className='flex flex-col items-center gap-6 p-6 md:flex-row md:items-start md:gap-8 md:p-8'>
            <div className='relative shrink-0'>
              <div className='flex h-28 w-28 items-center justify-center overflow-hidden rounded-2xl border border-zinc-200 bg-slate-100 shadow-md dark:border-zinc-800 dark:bg-zinc-800'>
                {onboarding.profile_photo_url ? (
                  <img
                    src={onboarding.profile_photo_url}
                    alt={fullName}
                    className='h-full w-full object-cover'
                  />
                ) : (
                  <IconUser className='dark:text-slate-650 h-12 w-12 text-slate-400' />
                )}
              </div>
            </div>

            <div className='min-w-0 flex-1 space-y-3 text-center md:text-left'>
              <div className='flex flex-wrap items-center justify-center gap-2.5 md:justify-start'>
                <h1 className='truncate text-2xl font-extrabold tracking-tight text-foreground md:text-3xl'>
                  {fullName || 'Candidate Profile'}
                </h1>
                {profile?.is_email_verified && (
                  <Badge
                    variant='secondary'
                    className='text-indigo-650 flex h-5 items-center gap-1 border-0 bg-indigo-50 px-2 font-bold dark:bg-indigo-950/40 dark:text-indigo-400'
                  >
                    <IconCircleCheck className='h-3.5 w-3.5' /> Verified
                  </Badge>
                )}
              </div>

              {onboarding.college && (
                <p className='flex items-center justify-center gap-1.5 text-sm font-semibold text-muted-foreground md:justify-start'>
                  <IconSchool className='h-4 w-4 text-slate-500' />{' '}
                  {onboarding.college}
                </p>
              )}

              <p className='flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground md:justify-start'>
                <IconMapPin className='h-4 w-4 text-slate-500' />
                {onboarding.current_address?.city &&
                onboarding.current_address?.state
                  ? `${onboarding.current_address.city}, ${onboarding.current_address.state}`
                  : 'Location not specified'}
              </p>

              {/* Dynamic Social Links Capsule Grid */}
              {Object.keys(socialLinks).length > 0 && (
                <div className='flex flex-wrap items-center justify-center gap-2 pt-2 md:justify-start'>
                  {Object.entries(socialLinks).map(([key, val]) => {
                    const url = String(val)
                    if (!url) return null
                    const info = PLATFORM_MAP[key] || {
                      label: key,
                      icon: IconLink,
                    }
                    const SocialIcon = info.icon
                    return (
                      <a
                        key={key}
                        href={url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='flex items-center gap-1.5 rounded-full border border-dashed border-zinc-200 bg-slate-50/50 px-3 py-1 text-xs font-medium text-slate-700 transition-all hover:bg-slate-100 hover:text-[#4035B4] dark:border-zinc-800 dark:bg-zinc-800/40 dark:text-slate-300 dark:hover:bg-zinc-800 dark:hover:text-indigo-400'
                      >
                        <SocialIcon className='h-3.5 w-3.5 text-slate-500' />
                        {info.label}
                      </a>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Quick Actions (Download Resume) */}
            {primaryResumeUrl && (
              <div className='shrink-0 pt-2 md:pt-0'>
                <a
                  href={primaryResumeUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex h-10 items-center justify-center gap-2 rounded-full bg-[#4035B4] px-5 text-xs font-bold text-white shadow-md transition-all hover:bg-[#4035B4]/90'
                >
                  <IconDownload className='h-4 w-4' />
                  Download Resume
                </a>
              </div>
            )}
          </div>
        </Card>

        {/* ── Content Grid ── */}
        <div className='grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_320px]'>
          {/* Main Column */}
          <div className='min-w-0 space-y-8'>
            {/* About / Profile Summary */}
            {onboarding.summary && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    About Me
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='dark:text-slate-350 whitespace-pre-line text-[14px] font-medium leading-relaxed text-slate-700'>
                    {onboarding.summary}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Work Experience Timeline */}
            {jobs.length > 0 && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Work Experience
                  </CardTitle>
                </CardHeader>
                <CardContent className='relative space-y-6 pl-6 before:absolute before:bottom-2 before:left-[11px] before:top-2 before:w-[2px] before:bg-slate-200 dark:before:bg-zinc-800'>
                  {jobs.map((job: any, index: number) => (
                    <div key={index} className='relative space-y-1.5'>
                      <div className='absolute -left-[20px] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#4035B4] shadow-sm dark:border-zinc-900' />

                      <div className='flex flex-wrap items-center justify-between gap-2'>
                        <h3 className='text-sm font-bold text-foreground'>
                          {job.role}
                        </h3>
                        <span className='inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-bold text-muted-foreground dark:bg-zinc-800'>
                          <IconCalendar className='h-3 w-3' />
                          {formatDate(job.start_date)}{' '}
                          {job.end_date
                            ? `to ${formatDate(job.end_date)}`
                            : job.is_current
                              ? '— Present'
                              : ''}
                        </span>
                      </div>

                      <p className='text-indigo-650 flex items-center gap-1.5 text-xs font-semibold dark:text-indigo-400'>
                        <IconBriefcase className='h-3.5 w-3.5 shrink-0 text-slate-500' />{' '}
                        {job.company}
                      </p>

                      {job.description && (
                        <p className='whitespace-pre-line pt-1 text-xs font-medium leading-relaxed text-slate-600 dark:text-slate-400'>
                          {job.description}
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Positions of Responsibility */}
            {pors.length > 0 && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Positions of Responsibility
                  </CardTitle>
                </CardHeader>
                <CardContent className='relative space-y-6 pl-6 before:absolute before:bottom-2 before:left-[11px] before:top-2 before:w-[2px] before:bg-slate-200 dark:before:bg-zinc-800'>
                  {pors.map((por: any, index: number) => (
                    <div key={index} className='relative space-y-1.5'>
                      <div className='absolute -left-[20px] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-indigo-500 shadow-sm dark:border-zinc-900' />

                      <div className='flex flex-wrap items-center justify-between gap-2'>
                        <h3 className='text-sm font-bold text-foreground'>
                          {por.role}
                        </h3>
                        <span className='inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-bold text-muted-foreground dark:bg-zinc-800'>
                          <IconCalendar className='h-3 w-3' />
                          {formatDate(por.issue_date || por.start_date)}{' '}
                          {por.end_date
                            ? `to ${formatDate(por.end_date)}`
                            : por.is_current
                              ? '— Present'
                              : ''}
                        </span>
                      </div>

                      {por.issuer && (
                        <p className='text-indigo-650 flex items-center gap-1.5 text-xs font-semibold dark:text-indigo-400'>
                          <IconUsers className='h-3.5 w-3.5 shrink-0 text-slate-500' />{' '}
                          {por.issuer}
                        </p>
                      )}

                      {por.description && (
                        <p className='whitespace-pre-line pt-1 text-xs font-medium leading-relaxed text-slate-600 dark:text-slate-400'>
                          {por.description}
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Projects Grid */}
            {projects && projects.length > 0 && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Featured Projects
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='grid gap-4 sm:grid-cols-2'>
                    {projects.map((project: any, index: number) => (
                      <div
                        key={index}
                        className='flex flex-col justify-between gap-3 rounded-xl border border-border bg-slate-50/50 p-4 transition-colors hover:bg-slate-100/50 dark:bg-zinc-900/50 dark:hover:bg-zinc-800/40'
                      >
                        <div className='min-w-0 space-y-1.5'>
                          <div className='flex items-center justify-between gap-2'>
                            <h4 className='truncate text-xs font-bold text-foreground'>
                              {project.title}
                            </h4>
                            {project.is_current && (
                              <Badge className='h-4 border-0 bg-emerald-500/10 text-[9px] font-bold text-emerald-600 dark:text-emerald-400'>
                                Active
                              </Badge>
                            )}
                          </div>
                          {project.domain && (
                            <span className='block font-mono text-[10px] font-bold uppercase tracking-wider text-muted-foreground'>
                              {project.domain}
                            </span>
                          )}
                          {project.description && (
                            <p className='line-clamp-3 pt-1 text-xs font-medium leading-normal text-muted-foreground/95'>
                              {project.description}
                            </p>
                          )}
                        </div>

                        {project.url && (
                          <a
                            href={project.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-indigo-650 inline-flex shrink-0 items-center gap-1 text-[10px] font-black uppercase hover:underline dark:text-indigo-400'
                          >
                            Live Demo <IconExternalLink className='h-3 w-3' />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Academic Accomplishments (Awards, Competitions, Scholarships, Test Scores) */}
            {(awards.length > 0 ||
              competitions.length > 0 ||
              scholarships.length > 0 ||
              testScores.length > 0) && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Awards & Accomplishments
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-6'>
                  {/* Awards & Recognitions */}
                  {awards.length > 0 && (
                    <div className='space-y-3.5'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconAward className='h-4 w-4 text-amber-500' /> Awards
                        & Recognitions
                      </h4>
                      <div className='grid gap-3 sm:grid-cols-2'>
                        {awards.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className='space-y-1 rounded-xl border border-border bg-slate-50/50 p-3 dark:bg-zinc-800/40'
                          >
                            <div className='flex items-start justify-between gap-1.5'>
                              <span className='text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              <span className='shrink-0 font-mono text-[9px] text-muted-foreground'>
                                {formatDate(item.issue_date || item.start_date)}
                              </span>
                            </div>
                            {item.issuer && (
                              <span className='text-indigo-650 block text-[10px] font-semibold dark:text-indigo-400'>
                                {item.issuer}
                              </span>
                            )}
                            {item.description && (
                              <p className='text-[11px] leading-relaxed text-muted-foreground'>
                                {item.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Competitions */}
                  {competitions.length > 0 && (
                    <div className='space-y-3.5 pt-2'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconTrophy className='h-4 w-4 text-yellow-500' />{' '}
                        Competitions
                      </h4>
                      <div className='grid gap-3 sm:grid-cols-2'>
                        {competitions.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className='space-y-1 rounded-xl border border-border bg-slate-50/50 p-3 dark:bg-zinc-800/40'
                          >
                            <div className='flex items-start justify-between gap-1.5'>
                              <span className='text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              <span className='shrink-0 font-mono text-[9px] text-muted-foreground'>
                                {formatDate(item.issue_date || item.start_date)}
                              </span>
                            </div>
                            {item.issuer && (
                              <span className='text-indigo-650 block text-[10px] font-semibold dark:text-indigo-400'>
                                {item.issuer}
                              </span>
                            )}
                            {item.description && (
                              <p className='text-[11px] leading-relaxed text-muted-foreground'>
                                {item.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Scholarships */}
                  {scholarships.length > 0 && (
                    <div className='space-y-3.5 pt-2'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconSchool className='h-4 w-4 text-emerald-500' />{' '}
                        Scholarships
                      </h4>
                      <div className='grid gap-3 sm:grid-cols-2'>
                        {scholarships.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className='space-y-1 rounded-xl border border-border bg-slate-50/50 p-3 dark:bg-zinc-800/40'
                          >
                            <div className='flex items-start justify-between gap-1.5'>
                              <span className='text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              <span className='shrink-0 font-mono text-[9px] text-muted-foreground'>
                                {formatDate(item.issue_date || item.start_date)}
                              </span>
                            </div>
                            {item.issuer && (
                              <span className='text-indigo-650 block text-[10px] font-semibold dark:text-indigo-400'>
                                {item.issuer}
                              </span>
                            )}
                            {item.description && (
                              <p className='text-[11px] leading-relaxed text-muted-foreground'>
                                {item.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Test Scores */}
                  {testScores.length > 0 && (
                    <div className='space-y-3.5 pt-2'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconTarget className='h-4 w-4 text-cyan-500' />{' '}
                        Standardized Tests
                      </h4>
                      <div className='grid gap-3 sm:grid-cols-3'>
                        {testScores.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className='flex flex-col justify-between gap-1.5 rounded-xl border border-border bg-slate-50/50 p-3 dark:bg-zinc-800/40'
                          >
                            <div className='space-y-0.5'>
                              <span className='block text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              {item.issuer && (
                                <span className='block text-[10px] font-semibold text-muted-foreground'>
                                  {item.issuer}
                                </span>
                              )}
                            </div>
                            {item.score && (
                              <div className='mt-1'>
                                <span className='text-indigo-650 rounded bg-indigo-50 px-2 py-0.5 text-[10px] font-bold dark:bg-indigo-950/30 dark:text-indigo-400'>
                                  Score: {item.score}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Research & Intellectual Property (Patents, Publications, Conferences) */}
            {(patents.length > 0 ||
              publications.length > 0 ||
              conferences.length > 0) && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Research & Publications
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-6'>
                  {/* Patents */}
                  {patents.length > 0 && (
                    <div className='space-y-3'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconShield className='h-4 w-4 text-purple-500' />{' '}
                        Patents
                      </h4>
                      <div className='space-y-3'>
                        {patents.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className='space-y-1.5 rounded-xl border border-border bg-slate-50/50 p-4 dark:bg-zinc-800/40'
                          >
                            <div className='flex items-start justify-between gap-2'>
                              <span className='text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              <span className='font-mono text-[9px] text-muted-foreground'>
                                {formatDate(item.issue_date || item.start_date)}
                              </span>
                            </div>
                            {item.licence_number && (
                              <span className='block font-mono text-[10px] text-muted-foreground'>
                                Patent No: {item.licence_number}
                              </span>
                            )}
                            {item.issuer && (
                              <span className='text-indigo-650 block text-[10px] font-semibold dark:text-indigo-400'>
                                {item.issuer}
                              </span>
                            )}
                            {item.description && (
                              <p className='text-xs leading-relaxed text-muted-foreground'>
                                {item.description}
                              </p>
                            )}
                            {item.url && (
                              <a
                                href={item.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-1 pt-1 text-[10px] font-bold text-[#4035B4] hover:underline'
                              >
                                View Patent{' '}
                                <IconExternalLink className='h-3 w-3' />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Publications */}
                  {publications.length > 0 && (
                    <div className='space-y-3 pt-2'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconBook className='h-4 w-4 text-indigo-500' /> Journal
                        & Conference Publications
                      </h4>
                      <div className='space-y-3'>
                        {publications.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className='space-y-1.5 rounded-xl border border-border bg-slate-50/50 p-4 dark:bg-zinc-800/40'
                          >
                            <div className='flex items-start justify-between gap-2'>
                              <span className='text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              <span className='font-mono text-[9px] text-muted-foreground'>
                                {formatDate(item.issue_date || item.start_date)}
                              </span>
                            </div>
                            {item.issuer && (
                              <span className='text-indigo-650 block text-[10px] font-semibold dark:text-indigo-400'>
                                {item.issuer}
                              </span>
                            )}
                            {item.description && (
                              <p className='text-xs leading-relaxed text-muted-foreground'>
                                {item.description}
                              </p>
                            )}
                            {item.url && (
                              <a
                                href={item.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-1 pt-1 text-[10px] font-bold text-[#4035B4] hover:underline'
                              >
                                Read Publication{' '}
                                <IconExternalLink className='h-3 w-3' />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Conferences & Workshops */}
                  {conferences.length > 0 && (
                    <div className='space-y-3 pt-2'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconPresentation className='h-4 w-4 text-blue-500' />{' '}
                        Conferences & Workshops
                      </h4>
                      <div className='grid gap-3 sm:grid-cols-2'>
                        {conferences.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className='space-y-1 rounded-xl border border-border bg-slate-50/50 p-3 dark:bg-zinc-800/40'
                          >
                            <div className='flex items-start justify-between gap-1.5'>
                              <span className='text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              <span className='shrink-0 font-mono text-[9px] text-muted-foreground'>
                                {formatDate(item.issue_date || item.start_date)}
                              </span>
                            </div>
                            {item.issuer && (
                              <span className='text-indigo-650 block text-[10px] font-semibold dark:text-indigo-400'>
                                {item.issuer}
                              </span>
                            )}
                            {item.description && (
                              <p className='text-[11px] leading-relaxed text-muted-foreground'>
                                {item.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Volunteering & Extracurriculars */}
            {(volunteering.length > 0 || extracurriculars.length > 0) && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Volunteering & Extracurriculars
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-6'>
                  {/* Volunteering */}
                  {volunteering.length > 0 && (
                    <div className='space-y-3'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconHeart className='h-4 w-4 text-rose-500' />{' '}
                        Volunteering
                      </h4>
                      <div className='before:bg-slate-250 dark:before:bg-zinc-850 relative space-y-3.5 pl-6 before:absolute before:bottom-2 before:left-[11px] before:top-2 before:w-[2px]'>
                        {volunteering.map((item: any, idx: number) => (
                          <div key={idx} className='relative space-y-1'>
                            <div className='absolute -left-[20px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-rose-500 shadow-sm dark:border-zinc-900' />
                            <div className='flex items-start justify-between gap-1.5'>
                              <span className='text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              <span className='bg-slate-150 rounded px-2 py-0.5 font-mono text-[9px] text-muted-foreground dark:bg-zinc-800'>
                                {formatDate(item.issue_date || item.start_date)}
                                {item.end_date
                                  ? ` to ${formatDate(item.end_date)}`
                                  : item.is_current
                                    ? ' — Present'
                                    : ''}
                              </span>
                            </div>
                            {item.issuer && (
                              <span className='text-indigo-650 block text-[10px] font-semibold dark:text-indigo-400'>
                                {item.issuer}
                              </span>
                            )}
                            {item.description && (
                              <p className='pt-0.5 text-xs leading-relaxed text-muted-foreground'>
                                {item.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Extracurriculars */}
                  {extracurriculars.length > 0 && (
                    <div className='space-y-3 pt-2'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200'>
                        <IconAward className='h-4 w-4 text-indigo-500' />{' '}
                        Extracurricular Activities
                      </h4>
                      <div className='grid gap-3 sm:grid-cols-2'>
                        {extracurriculars.map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className='space-y-1 rounded-xl border border-border bg-slate-50/50 p-3 dark:bg-zinc-800/40'
                          >
                            <div className='flex items-start justify-between gap-1.5'>
                              <span className='text-xs font-bold text-foreground'>
                                {item.role}
                              </span>
                              <span className='shrink-0 font-mono text-[9px] text-muted-foreground'>
                                {formatDate(item.issue_date || item.start_date)}
                              </span>
                            </div>
                            {item.issuer && (
                              <span className='text-indigo-650 block text-[10px] font-semibold dark:text-indigo-400'>
                                {item.issuer}
                              </span>
                            )}
                            {item.description && (
                              <p className='text-[11px] leading-relaxed text-muted-foreground'>
                                {item.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* GitHub Heatmap Stats */}
            {githubStats?.heatmap_data && (
              <Card className='overflow-hidden rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Open Source Contributions
                  </CardTitle>
                </CardHeader>
                <CardContent className='overflow-x-auto'>
                  <div className='min-w-[700px] p-2'>
                    <ContributionHeatmap data={githubStats.heatmap_data} />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar Column */}
          <div className='space-y-8'>
            {/* Contact Card */}
            <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                  Contact Details
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                {onboarding.primary_email && (
                  <div className='flex min-w-0 items-center gap-2.5'>
                    <IconMail className='h-4 w-4 shrink-0 text-slate-500' />
                    <a
                      href={`mailto:${onboarding.primary_email}`}
                      className='text-indigo-650 truncate text-xs font-semibold hover:underline dark:text-indigo-400'
                    >
                      {onboarding.primary_email}
                    </a>
                  </div>
                )}
                {onboarding.mobile_number && (
                  <div className='flex min-w-0 items-center gap-2.5'>
                    <IconPhone className='h-4 w-4 shrink-0 text-slate-500' />
                    <span className='dark:text-slate-350 text-xs font-semibold text-slate-700'>
                      {onboarding.mobile_number}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Skills & Radar */}
            {skills && skills.length > 0 && (
              <Card className='overflow-hidden rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Skills & Keywords
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  {skillData.length >= 3 && (
                    <div className='flex justify-center rounded-xl border border-zinc-100 bg-slate-50/50 p-2 dark:border-zinc-800 dark:bg-zinc-900/50'>
                      <SkillRadar skills={skillData} size={220} />
                    </div>
                  )}
                  <div className='flex flex-wrap gap-1.5'>
                    {skills.map((skill: any, index: number) => (
                      <Badge
                        key={index}
                        variant='outline'
                        className='rounded-md border border-zinc-200 bg-slate-50/50 px-2 py-0.5 text-xs font-semibold dark:border-zinc-800 dark:bg-zinc-800/40'
                      >
                        {skill.name}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Languages & Subjects */}
            {(languages.length > 0 || subjects.length > 0) && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Languages & Subjects
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-5'>
                  {/* Languages */}
                  {languages.length > 0 && (
                    <div className='space-y-2'>
                      <span className='block text-[10px] font-black uppercase tracking-wider text-muted-foreground'>
                        Known Languages
                      </span>
                      <div className='flex flex-wrap gap-1.5'>
                        {languages.map((lang: any, index: number) => (
                          <div
                            key={index}
                            className='flex items-center gap-1 rounded-md border border-zinc-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold dark:border-zinc-800 dark:bg-zinc-800/40'
                          >
                            <span>{lang.name}</span>
                            <span className='py-0.2 shrink-0 rounded bg-[#4035B4]/10 px-1 text-[8px] font-bold uppercase text-[#4035B4] dark:bg-[#4035B4]/20 dark:text-indigo-400'>
                              {lang.proficiency}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subjects */}
                  {subjects.length > 0 && (
                    <div className='space-y-2'>
                      <span className='block text-[10px] font-black uppercase tracking-wider text-muted-foreground'>
                        Core Electives & Subjects
                      </span>
                      <div className='flex flex-wrap gap-1.5'>
                        {subjects.map((sub: any, index: number) => (
                          <div
                            key={index}
                            className='flex items-center gap-1 rounded-md border border-zinc-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold dark:border-zinc-800 dark:bg-zinc-800/40'
                          >
                            <span>{sub.name}</span>
                            <span className='py-0.2 shrink-0 rounded bg-slate-100 px-1 text-[8px] font-bold uppercase text-zinc-500 dark:bg-zinc-800'>
                              {sub.proficiency}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Education Summary */}
            {((currentEdu && Object.keys(currentEdu).length > 0) ||
              previousEdu.length > 0) && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Education
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4 divide-y divide-border'>
                  {/* Current University */}
                  {currentEdu.degree && (
                    <div className='space-y-1 pt-0'>
                      <h4 className='text-xs font-bold text-foreground'>
                        {currentEdu.degree}{' '}
                        {currentEdu.branch ? `— ${currentEdu.branch}` : ''}
                      </h4>
                      <p className='flex items-center gap-1 text-[11px] font-semibold text-muted-foreground'>
                        <IconSchool className='h-3.5 w-3.5' />{' '}
                        {currentEdu.university}
                      </p>
                      {currentEdu.current_cgpa && (
                        <span className='text-indigo-650 inline-block rounded bg-indigo-50/50 px-1.5 py-0.5 text-[10px] font-bold dark:bg-indigo-950/20 dark:text-indigo-400'>
                          CGPA: {currentEdu.current_cgpa}/
                          {currentEdu.max_cgpa || '10'}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Previous Schools */}
                  {previousEdu.map((edu: any, index: number) => (
                    <div key={index} className='space-y-1 pt-3'>
                      <h4 className='text-xs font-bold text-foreground'>
                        {edu.level}{' '}
                        {edu.specialization ? `— ${edu.specialization}` : ''}
                      </h4>
                      <p className='flex items-center gap-1 text-[11px] font-semibold text-muted-foreground'>
                        <IconSchool className='h-3.5 w-3.5' />{' '}
                        {edu.school_name || edu.board}
                      </p>
                      {(edu.cgpa || edu.percentage || edu.obtained_marks) && (
                        <span className='inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground dark:bg-zinc-800'>
                          Score:{' '}
                          {edu.score_type === 'cgpa' ||
                          (!edu.score_type && edu.cgpa)
                            ? `${edu.cgpa}/10 CGPA`
                            : edu.score_type === 'marks' ||
                                (!edu.score_type && edu.obtained_marks)
                              ? `${edu.obtained_marks}/${edu.max_marks} MARKS`
                              : `${edu.percentage || edu.cgpa}%`}
                        </span>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {certifications.length > 0 && (
              <Card className='rounded-2xl border border-border bg-white shadow-sm dark:bg-zinc-900'>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-sm font-black uppercase tracking-wider text-muted-foreground'>
                    Certifications
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-3'>
                  {certifications.map((cert: any, index: number) => (
                    <div key={index} className='space-y-0.5'>
                      <h4 className='flex items-center gap-1.5 text-xs font-bold text-foreground'>
                        <IconCertificate className='h-4 w-4 shrink-0 text-[#4035B4]' />
                        {cert.role || cert.title}
                      </h4>
                      <p className='pl-5.5 text-[10px] font-semibold text-muted-foreground'>
                        {cert.issuer || cert.company}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
